/**
 * SearchPanel — what opens under the wall's search field (plan `2026_11_01_wall_search_panel.md`, boards 17 and 18).
 * `HeaderSearch` owns the field, the focus and the keyboard; this draws the sections for what stands in the field and
 * hands every tap back. Three layouts: the desktop dropdown under the field, the phone's full screen panel, and the
 * phone's half sheet that hangs from the top once a pill is set.
 *
 * The section order is the board's: Recent, then the facet groups; under typed words the rows; at request length the
 * closest pictures; under pills the things that sit with them; and the Make row, always last.
 */
import {useEffect, useLayoutEffect, useRef, useState} from 'react'

import type {FacetOffer} from '@core/wall/facetOffer'
import type {HouseFacets} from '@core/wall/facets'
import type {SuggestRow} from '@core/wall/suggest'
import {parseQuery} from '@core/wall/tsQuery'

import {MoreChip, OfferedChip, StaticPill} from './SearchTokens'
import {
    offeredGroups, typedSpans, visibleChips, yoursOf, withHeading,
    type FieldToken, type MakeRow, type OfferedGroup, type PanelBody,
} from './searchModel'

import styles from './SearchPanel.module.scss'

export type PanelLayout = 'dropdown' | 'full' | 'half'

export type SearchPanelProps = {
    /** The listbox's id, for the field's `aria-controls`. */
    id: string
    layout: PanelLayout
    body: PanelBody
    facets: HouseFacets
    shares: Record<string, number>
    yours: readonly FieldToken[]
    /** The lean words standing in the field: not offered again. */
    lean: readonly FieldToken[]
    /** The server's ranking of each group's values for this visitor and day (`facetOffer.ts`). */
    facetOffer?: FacetOffer
    words: string
    recent: readonly string[]
    /** The chips that sit with the pills (`searchModel.withThese`). */
    offered: readonly string[]
    rows: readonly SuggestRow[]
    /** The row the keyboard is on; -1 when it is on none. */
    current: number
    make: MakeRow
    optionId: (i: number) => string
    onClearRecent: () => void
    onRemoveRecent: (at: number) => void
    onPickRecent: (q: string) => void
    onPickFacet: (token: string) => void
    onPickWith: (phrase: string) => void
    onPickRow: (row: SuggestRow) => void
    onHoverRow: (i: number) => void
    onMake: () => void
}

const CROSS = (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6 L18 18 M18 6 L6 18" />
    </svg>
)

function Marked({label, spans}: {label: string; spans: readonly [number, number][]}) {
    const parts: React.ReactNode[] = []
    let at = 0
    spans.forEach(([from, to], i) => {
        if (from < at) return
        if (from > at) parts.push(label.slice(at, from))
        parts.push(<b key={i}>{label.slice(from, to)}</b>)
        at = to
    })
    if (at < label.length) parts.push(label.slice(at))
    return <>{parts}</>
}

function Thumb({src}: {src?: string}) {
    return (
        <span className={src ? styles.thumb : `${styles.thumb} ${styles.thumbEmpty}`} aria-hidden="true">
            {src && <img src={src} alt="" decoding="async" />}
        </span>
    )
}

/** A Recent entry read back as the pills it is: the phrases, the facet values, and the typed words as one more. */
function recentTokens(q: string, facets: HouseFacets): {key: string; label: string; swatch?: string}[] {
    const parsed = parseQuery(q)
    const tokens = yoursOf(parsed, facets).map((t) => ({key: t.id, label: t.label, swatch: t.swatch}))
    const words = parsed.words.trim()
    return words ? [...tokens, {key: `words:${words}`, label: words}] : tokens
}

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * One facet group's chips: they wrap, and a group of more than two lines ends its second line with a `+N` chip that
 * opens it in place. The chips are measured once on screen (`visibleChips` places the cut), so the first render of a
 * group shows all of them.
 */
function ChipGroup({values, onPick}: {values: OfferedGroup['values']; onPick: (token: string) => void}) {
    const key = values.map((v) => v.token).join('|')
    const box = useRef<HTMLDivElement>(null)
    const probe = useRef<HTMLDivElement>(null)
    const measured = useRef<{key: string; widths: number[]; more: number} | null>(null)
    const [cut, setCut] = useState<{key: string; n: number} | null>(null)
    const [open, setOpen] = useState(false)

    useIsoLayoutEffect(() => {
        const el = box.current
        if (!el) return
        const place = () => {
            if (!el.clientWidth) return
            if (measured.current?.key !== key) {
                const widths = [...el.querySelectorAll<HTMLElement>('[data-chip]')].map((c) => c.offsetWidth)
                measured.current = {key, widths, more: probe.current?.offsetWidth ?? 0}
            }
            const gap = parseFloat(getComputedStyle(el).columnGap) || 0
            setCut({key, n: visibleChips(measured.current.widths, el.clientWidth, gap, measured.current.more)})
        }
        place()
        if (typeof ResizeObserver === 'undefined') return
        const watch = new ResizeObserver(place)
        watch.observe(el)
        return () => watch.disconnect()
    }, [key])

    const n = cut && cut.key === key && !open ? cut.n : values.length
    return (
        <div ref={box} className={styles.chips}>
            {values.slice(0, n).map((v) => (
                <OfferedChip key={v.token} label={v.label} swatch={v.swatch} onClick={() => onPick(v.token)} />
            ))}
            {n < values.length && <MoreChip count={values.length - n} onClick={() => setOpen(true)} />}
            <div ref={probe} className={styles.probe} aria-hidden="true" inert><MoreChip count={values.length} /></div>
        </div>
    )
}

function Rest(props: SearchPanelProps) {
    const taken = [...props.yours, ...props.lean].filter((t) => t.ref.type !== 'phrase').map((t) => t.ref.value)
    const groups = offeredGroups(props.facets, props.shares, taken, props.facetOffer)
    return (
        <>
            {props.recent.length > 0 && (
                <section className={styles.sec} aria-label="Recent">
                    <div className={styles.head}>
                        <h2 className={styles.name}>Recent</h2>
                        <button type="button" className={styles.act} onClick={props.onClearRecent}>Clear</button>
                    </div>
                    {props.recent.map((q, at) => (
                        <div key={q} className={styles.recent}>
                            <button type="button" className={styles.recentPick} onClick={() => props.onPickRecent(q)} aria-label={`Search again: ${recentTokens(q, props.facets).map((t) => t.label).join(', ')}`}>
                                {recentTokens(q, props.facets).map((t) => <StaticPill key={t.key} label={t.label} swatch={t.swatch} />)}
                            </button>
                            <button type="button" className={styles.recentX} aria-label="Remove this search" onClick={() => props.onRemoveRecent(at)}>{CROSS}</button>
                        </div>
                    ))}
                </section>
            )}
            <section className={styles.sec} aria-label="Browse by">
                {groups.map((group) => (
                    <div key={group.key} className={styles.group}>
                        <h2 className={`${styles.name} ${styles.groupName}`}>{group.label}</h2>
                        <ChipGroup values={group.values} onPick={props.onPickFacet} />
                    </div>
                ))}
            </section>
        </>
    )
}

function With(props: SearchPanelProps) {
    if (props.offered.length === 0) return null
    return (
        <section className={styles.sec} aria-label={withHeading(props.yours)}>
            <div className={styles.head}><h2 className={styles.name}>{withHeading(props.yours)}</h2></div>
            <div className={styles.chips}>
                {props.offered.map((phrase) => <OfferedChip key={phrase} label={phrase} plus onClick={() => props.onPickWith(phrase)} />)}
            </div>
        </section>
    )
}

export function SearchPanel(props: SearchPanelProps) {
    const {body, rows, current, layout, make} = props
    const kind = body === 'closest' ? 'closest' : 'typed'
    const shown = rows.filter((r) => (kind === 'closest' ? r.kind === 'closest' : r.kind !== 'closest'))
    const typedTail = (replaces: number | undefined) => props.words.trim().split(/\s+/).slice(-(replaces ?? 1)).join(' ')

    return (
        <div className={styles.panel} data-layout={layout} id={props.id}>
            <div className={styles.scroll}>
                {body === 'rows' && shown.length > 0 && (
                    <ul className={styles.rows} role="listbox" aria-label="Suggestions" id={`${props.id}-rows`}>
                        {shown.map((row, i) => (
                            // Options are driven from the combobox input (aria-activedescendant and its keys); a click is the pointer's way in.
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                            <li
                                key={`${row.kind}:${row.pill}`}
                                id={props.optionId(i)}
                                role="option"
                                aria-selected={i === current}
                                className={i === current ? `${styles.row} ${styles.rowOn}` : styles.row}
                                onMouseMove={() => props.onHoverRow(i)}
                                onClick={() => props.onPickRow(row)}
                            >
                                <Thumb src={row.thumb} />
                                <span className={styles.rowLabel}><Marked label={row.label} spans={typedSpans(row.label, typedTail(row.replaces))} /></span>
                                <span className={styles.kind}>{row.kind === 'facet' ? (row.facet ?? '').toLowerCase() : 'phrase'}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {body === 'closest' && shown.length > 0 && (
                    <section className={`${styles.sec} ${styles.bare}`} aria-label="Closest on the wall">
                        <div className={styles.head}><h2 className={styles.name}>Closest on the wall</h2></div>
                        <ul className={styles.closest} role="listbox" aria-label="Closest on the wall" id={`${props.id}-rows`}>
                            {shown.map((row, i) => (
                                // Options are driven from the combobox input (aria-activedescendant and its keys); a click is the pointer's way in.
                                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                                <li
                                    key={row.pill}
                                    id={props.optionId(i)}
                                    role="option"
                                    aria-selected={i === current}
                                    className={i === current ? `${styles.row} ${styles.rowOn}` : styles.row}
                                    onMouseMove={() => props.onHoverRow(i)}
                                    onClick={() => props.onPickRow(row)}
                                >
                                    <Thumb src={row.thumb} />
                                    <span className={styles.rowLabel}><Marked label={row.label} spans={row.match ?? []} /></span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
                {body === 'with' && <With {...props} />}
                {(body === 'rest' || (body === 'with' && layout === 'full')) && <Rest {...props} />}
            </div>
            <MakeRowView {...props} make={make} />
        </div>
    )
}

function MakeRowView({make, onMake, layout}: Pick<SearchPanelProps, 'make' | 'onMake' | 'layout'>) {
    const inner = (
        <>
            <span className={styles.ghostFrame} aria-hidden="true" />
            <span className={styles.makeText}>
                {make.lead}<b>{make.strong}</b>{make.tail}
            </span>
            {make.hint && <span className={styles.hint}>{make.hint}</span>}
        </>
    )
    const className = make.lit ? `${styles.make} ${styles.makeLit}` : styles.make
    if (make.state === 'empty') return <div className={className} data-layout={layout}>{inner}</div>
    return <button type="button" className={className} data-layout={layout} onClick={onMake}>{inner}</button>
}
